// L'appli native (iOS, empaquetée avec Capacitor, voir capacitor.config.json et le dossier ios/) se reconnaît à l'objet `window.Capacitor` que la couche native injecte dans la
// page ; sur le web, il n'existe pas. On ne l'importe pas : le code web n'a pas besoin de @capacitor/core pour poser la question.
//   - pas de service worker (WKWebView sert la page depuis capacitor://localhost, où il n'est pas pris en charge ; l'appli est déjà dans le paquet) ;
//   - pas d'invitation à « installer l'appli » (elle l'est) : ni popup, ni fenêtre bloquante en paysage, ni lien ;
//   - l'appli iOS est toujours en « édition iOS » (une charge rapide offerte par niveau), sans le réglage de simulation ;
//   - dans l'appli Android (Google Play, dossier android/), la WebView ne sait pas partager : la carte passe par le plugin
//     ZeroKamiNative de l'appli (callNative ci-dessous, ui/shareModal.js).

/** Tourne-t-on dans une appli native (n'importe quelle plate-forme) ? @param {Window} [win] */
export function isNativeApp(win = typeof window === 'undefined' ? undefined : window) {
  return win?.Capacitor?.isNativePlatform?.() === true;
}

/** Tourne-t-on dans l'appli native iOS ? @param {Window} [win] */
export function isNativeIos(win = typeof window === 'undefined' ? undefined : window) {
  return isNativeApp(win) && win.Capacitor.getPlatform?.() === 'ios';
}

/** Tourne-t-on dans l'appli native Android (Google Play) ? @param {Window} [win] */
export function isNativeAndroid(win = typeof window === 'undefined' ? undefined : window) {
  return isNativeApp(win) && win.Capacitor.getPlatform?.() === 'android';
}

/**
 * Appelle une méthode de l'appli native par le pont de Capacitor (`nativePromise`), toujours sans @capacitor/core : dans l'appli
 * Google Play, le plugin ZeroKamiNative (android/…/ZeroKamiNativePlugin.java, #4) ouvre la feuille de partage d'Android ; dans les
 * deux applis, il donne le retour haptique du déclencheur (haptic ci-dessous ; ios/App/App/ZeroKamiNativePlugin.swift, #5).
 * Un refus du natif arrive en Error avec son `code` (ex. 'cancelled').
 * @param {string} plugin @param {string} method @param {object} [options] @param {Window} [win]
 * @returns {Promise<any>}
 */
export function callNative(plugin, method, options = {}, win = typeof window === 'undefined' ? undefined : window) {
  const cap = win?.Capacitor;
  if (typeof cap?.nativePromise !== 'function') return Promise.reject(new Error(`${plugin}.${method}: no native app`));
  return cap.nativePromise(plugin, method, options);
}

/**
 * Retour haptique du déclencheur dans les applis (#5) : 'shutter', un petit choc à chaque photo ; 'capture', une vibration de réussite
 * quand un kami est pris. Rien sur le web, ni si le natif échoue : le jeu n'en dépend pas.
 * @param {'shutter'|'capture'} kind @param {Window} [win]
 */
export function haptic(kind, win = typeof window === 'undefined' ? undefined : window) {
  if (!isNativeApp(win)) return;
  callNative('ZeroKamiNative', 'haptic', { kind }, win).catch(() => {});
}
